import _ from 'lodash';
import moment from 'moment';
import { QuickPriceCalculatorTypeLister } from '../pricelist/quickprice_lister';
import { QuickPriceCalculatorTypeWidget } from '../pricelist/quickprice_widget';
import { ICalcLineItem, ICalcOptions, EContent_HotelServiceTypeAssignment } from '../pricelist/quickprice';
import { BillablePackageKind, ComposedPackageKind, decomposeParticipantDayPackages, HotelPackageOptions } from './participantPackageDecompose';
import { MealDirectPayKey, resolveMealDirectPay, splitParticipantDayByPayment } from './participantPackageDiff';

export type EAM_EventProductLookupCodes =
  | 'TAGESPAUSCHALE'
  | 'HALBTAGESPAUSCHALE'
  | 'ADDON_COFFEE'
  | 'ADDON_COFFEEBREAK'
  | 'ADDON_LUNCH'
  | 'ADDON_DINNER'
  | 'ROOM_EZ'
  | 'ROOM_DZ'
  | 'VOLLPENSIONSPAUSCHALE_EZ'
  | 'VOLLPENSIONSPAUSCHALE_DZ';
export type EAM_EventRoomType = 'ROOM_DOUBLE' | 'ROOM_SINGLE' | 'ROOM_NONE';

const _allLookupCodes: EAM_EventProductLookupCodes[] = [
  'TAGESPAUSCHALE',
  'HALBTAGESPAUSCHALE',
  'ADDON_COFFEE',
  'ADDON_COFFEEBREAK',
  'ADDON_LUNCH',
  'ADDON_DINNER',
  'ROOM_EZ',
  'ROOM_DZ',
  'VOLLPENSIONSPAUSCHALE_EZ',
  'VOLLPENSIONSPAUSCHALE_DZ',
];
const _prevdayLookupCodes: EAM_EventProductLookupCodes[] = ['ROOM_EZ', 'ROOM_DZ', 'ADDON_DINNER'];

export interface HotelPackageOptionsParams {
  date: Date;
  guestCount?: number | null;
  isPremium?: boolean;
  publicCompanyId?: number;
}

/**
 * Full Board combinations must be activated in the hotel's base price list, which in practice means
 * the combination SKU is priceable on that date — catalog presence alone is not activation.
 */
export const hotelPackageOptionsFromQp = (
  qp: QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget,
  params: HotelPackageOptionsParams,
): HotelPackageOptions => {
  const _enabled = (code: EAM_EventProductLookupCodes) => {
    const product = qp.findPricedProductByLookupCode(code, params);
    if (product && qp.hasProductPrice(product.id, params)) return true;
    // Bundle-priced catalogs expose no product row, so presence stays the only available signal.
    return (qp.allBundlesDb || []).some(b => b.lookupCode === code);
  };

  return {
    singleRoomComboEnabled: _enabled('VOLLPENSIONSPAUSCHALE_EZ'),
    doubleRoomComboEnabled: _enabled('VOLLPENSIONSPAUSCHALE_DZ'),
  };
};

interface IProfileAttendance extends IDirectPay {
  days: IProfileAttendanceDay[];
}

export interface IProfileAttendanceDay extends IAttendance, IShow, IDirectPay {
  day: number;
}

export interface IAttendance {
  attending?: boolean | null;
  morning?: boolean | null;
  forenoon?: boolean | null;
  lunch?: boolean | null;
  afternoon?: boolean | null;
  dinner?: boolean | null;
  room?: EAM_EventRoomType | null;
}
interface IShow {
  morningShow?: boolean | null;
  forenoonShow?: boolean | null;
  lunchShow?: boolean | null;
  afternoonShow?: boolean | null;
  dinnerShow?: boolean | null;
  roomShow?: boolean | null;
}

interface IDirectPay {
  serviceDirectPay?: boolean | null;
  morningDirectPay?: boolean | null;
  forenoonDirectPay?: boolean | null;
  lunchDirectPay?: boolean | null;
  afternoonDirectPay?: boolean | null;
  dinnerDirectPay?: boolean | null;
  roomDirectPay?: boolean | null;
  extraDirectPay?: boolean | null;
  parkingDirectPay?: boolean | null;
}

export const isValidAttendanceLookupCode = (code: string): code is EAM_EventProductLookupCodes => {
  return _allLookupCodes.findIndex(a => a === code) >= 0;
};

export const mapAttendanceRegistered = (lookupCode: EAM_EventProductLookupCodes, aday: IAttendance & { day: number }) => {
  if (!!aday.attending) {
    if (lookupCode === 'TAGESPAUSCHALE') {
      return aday.forenoon && aday.afternoon;
    }
    if (lookupCode === 'HALBTAGESPAUSCHALE') {
      return (aday.forenoon || aday.afternoon) && !(aday.forenoon && aday.afternoon);
    }
    if (lookupCode === 'ADDON_COFFEEBREAK') {
      return aday.forenoon || aday.afternoon;
    }
    if (lookupCode === 'ADDON_COFFEE') {
      return !!aday.morning;
    }
    if (lookupCode === 'ADDON_LUNCH') {
      return !!aday.lunch;
    }
    if (lookupCode === 'ADDON_DINNER') {
      return !!aday.dinner;
    }
    if (lookupCode === 'VOLLPENSIONSPAUSCHALE_EZ') {
      return aday.room === 'ROOM_SINGLE' && !!aday.forenoon && !!aday.afternoon && !!aday.lunch && !!aday.dinner;
    }
    if (lookupCode === 'VOLLPENSIONSPAUSCHALE_DZ') {
      return aday.room === 'ROOM_DOUBLE' && !!aday.forenoon && !!aday.afternoon && !!aday.lunch && !!aday.dinner;
    }
  } else {
    if (aday.day < 0) {
      if (lookupCode === 'ADDON_DINNER') {
        return !!aday.dinner;
      }
    }
  }
  if (lookupCode === 'ROOM_DZ') {
    return aday.room === 'ROOM_DOUBLE';
  }
  if (lookupCode === 'ROOM_EZ') {
    return aday.room === 'ROOM_SINGLE';
  }
  return false;
};

const _mapAttendance2SKUs = (aday: IProfileAttendanceDay, qp: QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget) => {
  const result: { sku: string; code: EAM_EventProductLookupCodes; count: number }[] = [];

  const _getSKU = (code: EAM_EventProductLookupCodes, assignment?: string) => {
    let st =
      (assignment && qp.allServiceTypesDb?.find(st => st.listerServiceTypeAssignment === assignment)) ||
      qp.allServiceTypesDb?.find(st => st.listerServiceTypeAssignment === undefined) ||
      qp.allServiceTypesDb?.[0];

    if (!st) return null;

    const stp = st.products.find(p =>
      p.product
        ? qp.allProductsDb?.find(ap => ap.lookupCode === code && ap.id === p.product!.id)?.sku
        : p.bundle
          ? qp.allBundlesDb?.find(ab => ab.lookupCode === code && ab.id === p.bundle!.id)?.sku
          : null,
    );
    if (stp && stp.product) return stp.product.sku;
    if (stp && stp.bundle) return stp.bundle.sku;

    const sta = st.addons.find(a => (a.product ? qp.allProductsDb?.find(aa => aa.lookupCode === code && aa.id === a.product!.id)?.sku : null));
    if (sta && sta.product) return sta.product.sku;

    return null;
  };
  const _addResult = (count: number, code: EAM_EventProductLookupCodes, assignment?: string) => {
    const sku = _getSKU(code, assignment);
    if (sku) {
      const existing = result.find(r => r.sku === sku);
      if (existing) {
        existing.count += count;
      } else {
        result.push({ sku, code, count });
      }
    }
  };

  _allLookupCodes.forEach(code => {
    _addResult(0, code, EContent_HotelServiceTypeAssignment.REGULAR);
    _addResult(0, code, EContent_HotelServiceTypeAssignment.MEDIUM);
    _addResult(0, code, EContent_HotelServiceTypeAssignment.SMALL);
  });

  if (aday.attending) {
    if (aday.forenoon && aday.lunch && aday.afternoon) {
      _addResult(1, 'TAGESPAUSCHALE', EContent_HotelServiceTypeAssignment.REGULAR);
      if (aday.morning) _addResult(1, 'ADDON_COFFEE', EContent_HotelServiceTypeAssignment.REGULAR);
      if (aday.dinner) _addResult(1, 'ADDON_DINNER', EContent_HotelServiceTypeAssignment.REGULAR);
      if (aday.room === 'ROOM_SINGLE') _addResult(1, 'ROOM_EZ', EContent_HotelServiceTypeAssignment.REGULAR);
      if (aday.room === 'ROOM_DOUBLE') _addResult(1, 'ROOM_DZ', EContent_HotelServiceTypeAssignment.REGULAR);
    } else if (aday.forenoon && aday.afternoon) {
      _addResult(1, 'TAGESPAUSCHALE', EContent_HotelServiceTypeAssignment.MEDIUM);
      if (aday.morning) _addResult(1, 'ADDON_COFFEE', EContent_HotelServiceTypeAssignment.MEDIUM);
      if (aday.dinner) _addResult(1, 'ADDON_DINNER', EContent_HotelServiceTypeAssignment.MEDIUM);
      if (aday.room === 'ROOM_SINGLE') _addResult(1, 'ROOM_EZ', EContent_HotelServiceTypeAssignment.MEDIUM);
      if (aday.room === 'ROOM_DOUBLE') _addResult(1, 'ROOM_DZ', EContent_HotelServiceTypeAssignment.MEDIUM);
    } else if (aday.forenoon || aday.afternoon) {
      _addResult(1, 'TAGESPAUSCHALE', EContent_HotelServiceTypeAssignment.SMALL);
      if (aday.lunch) _addResult(1, 'ADDON_LUNCH', EContent_HotelServiceTypeAssignment.SMALL);
      if (aday.morning) _addResult(1, 'ADDON_COFFEE', EContent_HotelServiceTypeAssignment.SMALL);
      if (aday.dinner) _addResult(1, 'ADDON_DINNER', EContent_HotelServiceTypeAssignment.SMALL);
      if (aday.room === 'ROOM_SINGLE') _addResult(1, 'ROOM_EZ', EContent_HotelServiceTypeAssignment.SMALL);
      if (aday.room === 'ROOM_DOUBLE') _addResult(1, 'ROOM_DZ', EContent_HotelServiceTypeAssignment.SMALL);
    }
  } else {
    if (aday.day < 0) {
      if (aday.dinner) _addResult(1, 'ADDON_DINNER');
    }
    if (aday.room === 'ROOM_SINGLE') _addResult(1, 'ROOM_EZ');
    if (aday.room === 'ROOM_DOUBLE') _addResult(1, 'ROOM_DZ');
  }

  return result;
};

/**
 * First explicitly set value wins; callers pass day → attendance → event.
 * Unset on every level means the company pays.
 */
export const resolveDirectPayFlag = (...values: (boolean | null | undefined)[]): boolean => {
  for (const value of values) {
    if (_.isBoolean(value)) return value;
  }
  return false;
};

const _directPay = (flag: keyof IDirectPay, g: IDirectPay, a: IDirectPay, aday: IDirectPay) => resolveDirectPayFlag(aday[flag], a[flag], g[flag]);

export interface PackageKindSkuRef {
  code: EAM_EventProductLookupCodes;
  /** `EContent_HotelServiceTypeAssignment` value; unset picks the hotel's default service type. */
  assignment?: string;
}

/**
 * How a half-day package is booked for this hotel's price list.
 * Prefer dedicated HALBTAGESPAUSCHALE, then classic TAGESPAUSCHALE MEDIUM, else Small + Lunch.
 */
export type HalfDaySkuMode = 'HALBTAGESPAUSCHALE' | 'TAGESPAUSCHALE_MEDIUM' | 'SMALL_PLUS_LUNCH';

/** Resolve half-day SKU mode from which lookup codes the hotel actually lists. */
export const resolveHalfDaySkuMode = (
  hasSku: (lookupCode: EAM_EventProductLookupCodes, assignment?: string) => boolean,
): HalfDaySkuMode => {
  if (hasSku('HALBTAGESPAUSCHALE', EContent_HotelServiceTypeAssignment.REGULAR) || hasSku('HALBTAGESPAUSCHALE')) {
    return 'HALBTAGESPAUSCHALE';
  }
  if (hasSku('TAGESPAUSCHALE', EContent_HotelServiceTypeAssignment.MEDIUM)) {
    return 'TAGESPAUSCHALE_MEDIUM';
  }
  return 'SMALL_PLUS_LUNCH';
};

/**
 * Composed package kind → the lookup codes the offer books for it.
 *
 * @param kind - Composed or billable kind (AM/PM variants book the same codes)
 * @param halfDayMode - Hotel half-day SKU availability (see {@link resolveHalfDaySkuMode})
 */
export const packageKindLookupCodes = (kind: ComposedPackageKind | BillablePackageKind, halfDayMode: HalfDaySkuMode): PackageKindSkuRef[] => {
  switch (kind) {
    case 'FULL_BOARD_SINGLE':
      return [{ code: 'VOLLPENSIONSPAUSCHALE_EZ', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
    case 'FULL_BOARD_DOUBLE':
      return [{ code: 'VOLLPENSIONSPAUSCHALE_DZ', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
    case 'FULL_DAY':
      return [{ code: 'TAGESPAUSCHALE', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
    case 'HALF_DAY':
    case 'HALF_DAY_AM_LUNCH':
    case 'HALF_DAY_PM_LUNCH':
      if (halfDayMode === 'HALBTAGESPAUSCHALE') {
        return [{ code: 'HALBTAGESPAUSCHALE', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
      }
      if (halfDayMode === 'TAGESPAUSCHALE_MEDIUM') {
        // Same SKU classic uses for “Tagespauschale Halbtags” when HALBTAGESPAUSCHALE is absent.
        return [{ code: 'TAGESPAUSCHALE', assignment: EContent_HotelServiceTypeAssignment.MEDIUM }];
      }
      return [
        { code: 'TAGESPAUSCHALE', assignment: EContent_HotelServiceTypeAssignment.SMALL },
        { code: 'ADDON_LUNCH', assignment: EContent_HotelServiceTypeAssignment.SMALL },
      ];
    case 'SMALL':
    case 'SMALL_AM':
    case 'SMALL_PM':
      return [{ code: 'TAGESPAUSCHALE', assignment: EContent_HotelServiceTypeAssignment.SMALL }];
    case 'WELCOME_COFFEE':
      return [{ code: 'ADDON_COFFEE', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
    case 'COFFEE_BREAK':
      return [{ code: 'ADDON_COFFEEBREAK', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
    case 'LUNCH':
      return [{ code: 'ADDON_LUNCH', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
    case 'DINNER':
      return [{ code: 'ADDON_DINNER', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
    case 'ROOM_SINGLE':
      return [{ code: 'ROOM_EZ', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
    case 'ROOM_DOUBLE':
      return [{ code: 'ROOM_DZ', assignment: EContent_HotelServiceTypeAssignment.REGULAR }];
    case 'EXTRAS':
    case 'PARKING':
      // No attendance lookup SKUs yet — leftovers stay UI-only.
      return [];
  }
};

/** Premium SKU for an attendance lookup code: service-type wiring first, catalog SKU as fallback. */
export const resolvePremiumAttendanceSKU = (
  qp: QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget,
  code: EAM_EventProductLookupCodes,
  assignment?: string,
): string | null => {
  const st =
    (assignment && qp.allServiceTypesDb?.find(st => st.listerServiceTypeAssignment === assignment)) ||
    qp.allServiceTypesDb?.find(st => st.listerServiceTypeAssignment === undefined) ||
    qp.allServiceTypesDb?.[0];

  if (!st) {
    // Fall back to catalog SKU when service-type wiring is incomplete (e.g. full-board).
    return qp.getSKUForLookupCode(code) || null;
  }

  const stp = st.products.find(p =>
    p.product
      ? qp.allProductsDb?.find(ap => ap.lookupCode === code && ap.id === p.product!.id)?.sku
      : p.bundle
        ? qp.allBundlesDb?.find(ab => ab.lookupCode === code && ab.id === p.bundle!.id)?.sku
        : null,
  );
  if (stp && stp.product) return stp.product.sku;
  if (stp && stp.bundle) return stp.bundle.sku;

  const sta = st.addons.find(a => (a.product ? qp.allProductsDb?.find(aa => aa.lookupCode === code && aa.id === a.product!.id)?.sku : null));
  if (sta && sta.product) return sta.product.sku;

  return qp.getSKUForLookupCode(code) || null;
};

/**
 * Premium greedy composition → SKUs. Classic must keep using `_mapAttendance2SKUs`.
 * Always split company vs on-site before compose (mixed payment breaks Full Board / day packages).
 * ignoreDirectPay true: merge both groups' SKUs (hotel offer still sees on-site).
 * ignoreDirectPay false: company-invoice SKUs only (invoices).
 */
const _mapAttendance2SKUsPremium = (
  g: IDirectPay,
  a: IProfileAttendance,
  aday: IProfileAttendanceDay,
  qp: QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget,
  ignoreDirectPay: boolean,
  hotel: HotelPackageOptions,
) => {
  const result: { sku: string; code: EAM_EventProductLookupCodes; count: number }[] = [];

  const _getSKU = (code: EAM_EventProductLookupCodes, assignment?: string) => resolvePremiumAttendanceSKU(qp, code, assignment);
  const _addResult = (count: number, code: EAM_EventProductLookupCodes, assignment?: string) => {
    const sku = _getSKU(code, assignment);
    if (sku) {
      const existing = result.find(r => r.sku === sku);
      if (existing) {
        existing.count += count;
      } else {
        result.push({ sku, code, count });
      }
    }
  };

  _allLookupCodes.forEach(code => {
    _addResult(0, code, EContent_HotelServiceTypeAssignment.REGULAR);
    _addResult(0, code, EContent_HotelServiceTypeAssignment.MEDIUM);
    _addResult(0, code, EContent_HotelServiceTypeAssignment.SMALL);
  });

  const { company, onSite } = splitParticipantDayByPayment(
    {
      ...aday,
      morningDirectPay: _directPay('morningDirectPay', g, a, aday),
      forenoonDirectPay: _directPay('forenoonDirectPay', g, a, aday),
      lunchDirectPay: _directPay('lunchDirectPay', g, a, aday),
      afternoonDirectPay: _directPay('afternoonDirectPay', g, a, aday),
      dinnerDirectPay: _directPay('dinnerDirectPay', g, a, aday),
      serviceDirectPay: mapAttendanceDirectPay('TAGESPAUSCHALE', g, a, aday),
      roomDirectPay: mapAttendanceDirectPay('ROOM_EZ', g, a, aday),
      extraDirectPay: _directPay('extraDirectPay', g, a, aday),
      parkingDirectPay: _directPay('parkingDirectPay', g, a, aday),
    },
    true,
  );
  const composed = [...decomposeParticipantDayPackages(company, hotel), ...(ignoreDirectPay ? decomposeParticipantDayPackages(onSite, hotel) : [])];

  const halfDayMode = resolveHalfDaySkuMode(
    (code, assignment) => !!(_getSKU(code, assignment) || (!assignment && _getSKU(code))),
  );

  for (const item of composed) {
    for (const ref of packageKindLookupCodes(item.kind, halfDayMode)) {
      _addResult(1, ref.code, ref.assignment);
    }
  }

  return result;
};

export const mapAttendanceShow = (lookupCode: EAM_EventProductLookupCodes, aday: IShow) => {
  const _cf = (flag: keyof IShow) => (_.isBoolean(aday[flag]) ? !!aday[flag] : true);

  if (lookupCode === 'TAGESPAUSCHALE' || lookupCode === 'HALBTAGESPAUSCHALE') {
    return _cf('forenoonShow') || _cf('afternoonShow') || _cf('lunchShow');
  }
  if (lookupCode === 'ADDON_COFFEE') {
    return _cf('morningShow');
  }
  if (lookupCode === 'ADDON_COFFEEBREAK') {
    return _cf('forenoonShow') || _cf('afternoonShow');
  }
  if (lookupCode === 'ADDON_LUNCH') {
    return _cf('lunchShow');
  }
  if (lookupCode === 'ADDON_DINNER') {
    return _cf('dinnerShow');
  }
  if (
    lookupCode === 'ROOM_DZ' ||
    lookupCode === 'ROOM_EZ' ||
    lookupCode === 'VOLLPENSIONSPAUSCHALE_EZ' ||
    lookupCode === 'VOLLPENSIONSPAUSCHALE_DZ'
  ) {
    return _cf('roomShow');
  }
  return true;
};

export const mapAttendanceDirectPay = (lookupCode: EAM_EventProductLookupCodes, g: IDirectPay, a: IDirectPay, aday: IDirectPay & IAttendance) => {
  const _cf = (flag: keyof IDirectPay) => _directPay(flag, g, a, aday);
  // Per-meal flags exist on the day only; without them this falls back to the category flag, so Classic is unaffected.
  const _meal = (meal: MealDirectPayKey) => resolveMealDirectPay({ ...aday, serviceDirectPay: _cf('serviceDirectPay') }, meal);

  if (lookupCode === 'TAGESPAUSCHALE' || lookupCode === 'HALBTAGESPAUSCHALE') {
    // A package only forms from services of a single payer, so it is on-site only when every service in it is.
    const meals: MealDirectPayKey[] = [];
    if (aday.forenoon) meals.push('forenoon');
    if (aday.afternoon) meals.push('afternoon');
    if (aday.lunch) meals.push('lunch');
    if (meals.length === 0) return _cf('serviceDirectPay');
    return meals.every(_meal);
  }
  if (lookupCode === 'ADDON_COFFEE') {
    return _meal('morning');
  }
  if (lookupCode === 'ADDON_COFFEEBREAK') {
    // One line item covers both breaks, so it mirrors the OR that `mapAttendanceShow` uses for the same code.
    return _meal('forenoon') || _meal('afternoon');
  }
  if (lookupCode === 'ADDON_LUNCH') {
    return _meal('lunch');
  }
  if (lookupCode === 'ADDON_DINNER') {
    return _meal('dinner');
  }
  if (
    lookupCode === 'ROOM_DZ' ||
    lookupCode === 'ROOM_EZ' ||
    lookupCode === 'VOLLPENSIONSPAUSCHALE_EZ' ||
    lookupCode === 'VOLLPENSIONSPAUSCHALE_DZ'
  ) {
    return _cf('roomDirectPay');
  }
  return false;
};

export const mapShowAttendance = (lookupCode: EAM_EventProductLookupCodes, shows: boolean[], attendances: IShow[]) => {
  return shows.map((show, index) => {
    const attendance = attendances[index];
    if (lookupCode === 'TAGESPAUSCHALE') {
      attendance.forenoonShow = show;
      attendance.afternoonShow = show;
    } else if (lookupCode === 'HALBTAGESPAUSCHALE') {
      attendance.forenoonShow = show;
    } else if (lookupCode === 'ADDON_COFFEE') {
      attendance.morningShow = show;
    } else if (lookupCode === 'ADDON_COFFEEBREAK') {
      attendance.forenoonShow = show;
    } else if (lookupCode === 'ADDON_LUNCH') {
      attendance.lunchShow = show;
    } else if (lookupCode === 'ADDON_DINNER') {
      attendance.dinnerShow = show;
    } else if (
      lookupCode === 'ROOM_DZ' ||
      lookupCode === 'ROOM_EZ' ||
      lookupCode === 'VOLLPENSIONSPAUSCHALE_EZ' ||
      lookupCode === 'VOLLPENSIONSPAUSCHALE_DZ'
    ) {
      attendance.roomShow = show;
    }
    return attendance;
  });
};

export const mapProfileAttendanceDay2LineItems = (
  g: IDirectPay,
  a: IProfileAttendance,
  day: IProfileAttendanceDay,
  qp: QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget,
  ignoreDirectPay: boolean,
  opts?: {
    isPremium?: boolean;
    /** Required for Premium: which Full Board combinations the hotel activated for that date. */
    hotelPackageOptions?: HotelPackageOptions;
  },
) => {
  const lineItems: { sku: string; code: EAM_EventProductLookupCodes; count: number }[] = [];

  const skus = opts?.isPremium
    ? _mapAttendance2SKUsPremium(
        g,
        a,
        day,
        qp,
        ignoreDirectPay,
        opts?.hotelPackageOptions || { singleRoomComboEnabled: false, doubleRoomComboEnabled: false },
      )
    : _mapAttendance2SKUs(day, qp);
  for (const sku of skus) {
    // Premium already split by per-cell DirectPay; Classic still skips via category flags.
    if (!ignoreDirectPay && !opts?.isPremium) {
      const show = mapAttendanceShow(sku.code, day);
      const directPay = mapAttendanceDirectPay(sku.code, g, a, day);

      if (directPay && show) continue;
    }

    const li = lineItems.find(l => l.sku === sku.sku);
    if (li) {
      li.count += sku.count;
    } else {
      lineItems.push({
        sku: sku.sku,
        code: sku.code,
        count: sku.count,
      });
    }
  }
  return lineItems;
};

export const mapLineItems2GuestCount = (
  lineItems: { sku: string; count: number }[],
  qp: QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget,
) => {
  const skusOvernight = _.uniq(
    [
      ...(qp.allBundlesDb || []).filter(b => b.lookupCode === 'ROOM_EZ').map(b => b.sku),
      ...(qp.allBundlesDb || []).filter(b => b.lookupCode === 'ROOM_DZ').map(b => b.sku),
      ...(qp.allBundlesDb || []).filter(b => b.lookupCode === 'VOLLPENSIONSPAUSCHALE_EZ').map(b => b.sku),
      ...(qp.allBundlesDb || []).filter(b => b.lookupCode === 'VOLLPENSIONSPAUSCHALE_DZ').map(b => b.sku),
      ...(qp.allProductsDb || []).filter(p => p.lookupCode === 'ROOM_EZ').map(p => p.sku),
      ...(qp.allProductsDb || []).filter(p => p.lookupCode === 'ROOM_DZ').map(p => p.sku),
      ...(qp.allProductsDb || []).filter(p => p.lookupCode === 'VOLLPENSIONSPAUSCHALE_EZ').map(p => p.sku),
      ...(qp.allProductsDb || []).filter(p => p.lookupCode === 'VOLLPENSIONSPAUSCHALE_DZ').map(p => p.sku),
    ]
      .filter(s => s)
      .map(s => s!),
  );

  const skusGuests = _.uniq(
    [
      ...(qp.allBundlesDb || []).filter(b => b.lookupCode === 'TAGESPAUSCHALE').map(b => b.sku),
      ...(qp.allBundlesDb || []).filter(b => b.lookupCode === 'HALBTAGESPAUSCHALE').map(b => b.sku),
      ...(qp.allBundlesDb || []).filter(b => b.lookupCode === 'VOLLPENSIONSPAUSCHALE_EZ').map(b => b.sku),
      ...(qp.allBundlesDb || []).filter(b => b.lookupCode === 'VOLLPENSIONSPAUSCHALE_DZ').map(b => b.sku),
      ...(qp.allProductsDb || []).filter(p => p.lookupCode === 'TAGESPAUSCHALE').map(p => p.sku),
      ...(qp.allProductsDb || []).filter(p => p.lookupCode === 'HALBTAGESPAUSCHALE').map(p => p.sku),
      ...(qp.allProductsDb || []).filter(p => p.lookupCode === 'VOLLPENSIONSPAUSCHALE_EZ').map(p => p.sku),
      ...(qp.allProductsDb || []).filter(p => p.lookupCode === 'VOLLPENSIONSPAUSCHALE_DZ').map(p => p.sku),
    ]
      .filter(s => s)
      .map(s => s!),
  );

  return {
    overnightGuests: lineItems.filter(li => skusOvernight.includes(li.sku)).reduce((agg, c) => agg + c.count, 0),
    totalGuests: lineItems.filter(li => skusGuests.includes(li.sku)).reduce((agg, c) => agg + c.count, 0),
  };
};

export const mapProfileAttendances2LineItems = (
  gevent: IDirectPay,
  attendances: IProfileAttendance[],
  startDate: string,
  qp: QuickPriceCalculatorTypeLister | QuickPriceCalculatorTypeWidget,
  params?: {
    includeEmpty?: boolean;
    ignoreDirectPay?: boolean;
    findPrice?: (day: number, sku: string) => ReturnType<NonNullable<ICalcOptions['priceSelector']>> | undefined;
    isPremium?: boolean;
    publicCompanyId?: number;
    hotelPackageOptions?: HotelPackageOptions;
  },
) => {
  let lineItems: ICalcLineItem[] = [];

  const days = _.uniq(attendances.reduce((agg, a) => [...agg, ...a.days.map(d => d.day)], [] as number[])).sort();
  for (const day of days) {
    const date = moment(startDate).add(day, 'days').toDate();

    // Combination activation is priced per date, so it is resolved inside the day loop.
    const hotelPackageOptions =
      params?.hotelPackageOptions ||
      (params?.isPremium
        ? hotelPackageOptionsFromQp(qp, {
            date,
            guestCount: attendances.length,
            isPremium: params?.isPremium,
            publicCompanyId: params?.publicCompanyId,
          })
        : undefined);

    for (const attendance of attendances) {
      const attendanceDay = attendance.days.find(d => d.day == day);
      if (!attendanceDay) continue;

      const daySkuCounts = mapProfileAttendanceDay2LineItems(gevent, attendance, attendanceDay, qp, !!params?.ignoreDirectPay, {
        isPremium: !!params?.isPremium,
        hotelPackageOptions,
      });

      for (const daySkuCount of daySkuCounts) {
        if (day < 0 && !_prevdayLookupCodes.includes(daySkuCount.code)) continue;

        const li = lineItems.find(l => l.sku === daySkuCount.sku && l.day === day);
        const itemCount = (li ? li.count : 0) + daySkuCount.count;
        const newLineItem = qp.getSKULineItem(
          daySkuCount.sku,
          {
            date,
            itemCount,
            guestCount: attendances.length,
            isPremium: params?.isPremium || false,
            publicCompanyId: params?.publicCompanyId || undefined,
          },
          null,
          {
            allowNoPrices: true,
            priceSelector: params?.findPrice ? sel => params.findPrice!(day, daySkuCount.sku) : undefined,
          },
        ); //tageweise guest count ?
        if (!newLineItem) continue;

        if (li) {
          lineItems = lineItems.filter(r => (r.day === li.day ? r.sku !== daySkuCount.sku : true));
        }
        newLineItem.day = day;
        lineItems.push(newLineItem);
      }
    }
    if (!!params?.includeEmpty) {
      for (const code of _allLookupCodes) {
        if (day < 0 && !_prevdayLookupCodes.includes(code)) continue;

        const sku = qp.getSKUForLookupCode(code);
        if (!sku) continue;

        const li = lineItems.find(l => l.sku === sku && l.day === day);
        if (!li) {
          const newLineItem = qp.getSKULineItem(
            sku,
            {
              date,
              itemCount: 0,
              guestCount: attendances.length,
              isPremium: params?.isPremium || false,
              publicCompanyId: params?.publicCompanyId || undefined,
            },
            null,
            {
              allowNoPrices: true,
              priceSelector: params?.findPrice ? sel => params.findPrice!(day, sku) : undefined,
            },
          );
          if (!newLineItem) continue;
          newLineItem.day = day;
          lineItems.push(newLineItem);
        }
      }
    }
  }
  return {
    lineItems,
    input: {
      prevdayGuests: mapLineItems2GuestCount(
        lineItems.filter(li => li.day === -1),
        qp,
      ).overnightGuests,
      days: days.map(day => ({
        day: day,
        overnightGuests: mapLineItems2GuestCount(
          lineItems.filter(li => li.day === day),
          qp,
        ).overnightGuests,
        totalGuests: mapLineItems2GuestCount(
          lineItems.filter(li => li.day === day),
          qp,
        ).totalGuests,
      })),
    },
  };
};
